// import { useState } from 'react'
// import axios from 'axios'
// import { setJwtToken } from '../config/axiosInstance'

// const useFileDownloader = () => {
//   const [loading, setLoading] = useState(false)

//   const downloadFile = async (url, params = {}) => {
//     try {
//       setLoading(true)

//       const response = await axios.get(url, {
//         params,
//         responseType: 'blob',
//         headers: setJwtToken().headers,
//       })

//       if (response.status === 200) {
//         const currentDate = new Date()
//         const formattedDate = currentDate.toISOString().slice(0, 10)
//         const rnFile = `${formattedDate}.xlsx`

//         const contentDisposition = response.headers['content-disposition']
//         const matches = /filename="([^"]+)"/.exec(contentDisposition)
//         const filename = matches ? matches[1] : `excel-file-${rnFile}` // Use rnFile here instead of 'excel-file.xlsx'

//         const fileURL = window.URL.createObjectURL(new Blob([response.data]))
//         const link = document.createElement('a')
//         link.href = fileURL
//         link.setAttribute('download', filename)
//         document.body.appendChild(link)
//         link.click()

//         window.URL.revokeObjectURL(fileURL)
//         document.body.removeChild(link)
//         setLoading(false)
//       } else {
//         setLoading(false)
//         throw new Error('File download failed')
//       }
//     } catch (error) {
//       setLoading(false)
//       console.error('Error downloading file:', error)
//     }
//   }

//   return { loading, downloadFile }
// }

// export default useFileDownloader

import { useState } from 'react'
import axiosLib from 'axios'
import { setJwtToken,instance as axios } from '../config/axiosInstance'
import { toastMessage } from '../utils/toastMessage'
import { trackActivityApi } from '../services/activityLog'

// Derive a readable module name from download URL + file name
const _downloadModule = (url = '', fileName = '') => {
  const s = (url + ' ' + fileName).toLowerCase()
  if (/bank.pan.not.approved|bank-pan-not-approved/i.test(s))              return 'Consultant Payout — Bank/PAN Not Approved'
  if (/user.payment.data|get-user-payments-sheet/i.test(s))                return 'Consultant Payout — User Payment Data'
  if (/payment.tracking|payment-tracking-sheet/i.test(s))                  return 'Consultant Payout — Consultant Payment Tracking'
  if (/settlement.template|settlement-template/i.test(s))                  return 'Consultant Payout — Settlement Template'
  if (/current.month.payout|current-month-payout/i.test(s))                return 'Consultant Payout — Current Month'
  if (/completed.consult|complete.*call|complete.*chat/i.test(s))        return 'Completed Consultations'
  if (/missed.call|missed.chat/i.test(s))                                return 'Missed Consultations/Chats'
  if (/failed.consult|failed.call/i.test(s))                             return 'Failed Consultations'
  if (/booked.waitlist/i.test(s))                                         return 'Booked Waitlist'
  if (/progress.consult|in.progress/i.test(s))                           return 'In-Progress Consultations'
  if (/delete.*user|deleted.user/i.test(s))                              return 'Deleted Users'
  if (/delete.*consult|deleted.consult/i.test(s))                        return 'Deleted Consultants'
  if (/payout.*export|export.*payout|payout/i.test(s))                   return 'Consultant Payout'
  if (/recharge.history/i.test(s))                                        return 'Recharge History'
  if (/spending.history/i.test(s))                                        return 'Spending History'
  if (/transaction/i.test(s))                                             return 'Transaction Management'
  if (/consultant.excel|consultant.file|consult.*download/i.test(s))     return 'Consultant Management'
  if (/online.offline/i.test(s))                                          return 'Online/Offline Consultants'
  if (/support/i.test(s))                                                 return 'Support Management'
  if (/flag/i.test(s))                                                    return 'Flag Requests'
  if (/request/i.test(s))                                                 return 'Request Management'
  if (/horoscope/i.test(s))                                               return 'Horoscope Management'
  if (/marketing/i.test(s))                                               return 'Marketing Management'
  if (/banner/i.test(s))                                                  return 'Banner Management'
  if (/notification/i.test(s))                                            return 'Notification Management'
  if (/analytics/i.test(s))                                               return 'Analytics Management'
  if (/waitlist/i.test(s))                                                return 'Waitlist Management'
  if (/user/i.test(s))                                                    return 'User Management'
  // Generic fallback: first meaningful URL segment
  const seg = url.toLowerCase().replace(/^\/admin\//, '').split('/')[0] || 'Admin Panel'
  return seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/** Readable section name for a download URL (OTP / activity logs). */
export function getModuleLabelForDownloadUrl(url = '', fileName = '') {
  return _downloadModule(url, fileName)
}

const useFileDownloader = () => {
  const [loading, setLoading] = useState(false)
  // let loading = false

  /**
   * @param {object|null} trackMeta - If set, `otpExport` + purpose/phoneLast4/activityModule enrich EXPORT activity log.
   */
  const downloadFile = async (url, fileName, params = {}, trackMeta = null) => {
    try {
      setLoading(true)

      const { headers } = setJwtToken()
      const response = await axios.get(url, {
        params,
        responseType: 'blob',
        headers,
        skipAuthLogout: true,
      })
      const contentType = String(
        response.headers['content-type'] || ''
      ).toLowerCase()

      if (response.status === 200) {
        if (contentType.includes('application/json')) {
          const text =
            typeof response.data?.text === 'function'
              ? await response.data.text()
              : String(response.data)
          let msg = 'Could not download file'
          try {
            const j = JSON.parse(text)
            msg = j.message || msg
          } catch {
            if (text) msg = text.slice(0, 200)
          }
          toastMessage(msg, 'error', 'fileDownload')
          setLoading(false)
          return false
        }

        const cd = response.headers['content-disposition'] || ''
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd)
        const serverName = m ? decodeURIComponent(m[1].trim()) : null
        const downloadName = serverName || fileName

        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream',
        })

        const blobUrl = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = downloadName

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        setLoading(false)

        // ── Activity log ──────────────────────────────────────────────────────
        try {
          const email = localStorage.getItem('vedic_email') || 'Admin'
          const module = _downloadModule(url, downloadName)
          const otp = trackMeta?.otpExport
          const passwordBypass = otp && trackMeta?.bypassMethod
          trackActivityApi({
            action: 'EXPORT',
            module,
            description: otp
              ? passwordBypass
                ? `${email} — password-approved export file saved: ${module} (purpose: ${trackMeta.purpose || 'Sheet Download'})`
                : `${email} — OTP export file saved: ${module} (purpose: ${trackMeta.purpose || 'Sheet Download'}, ***${trackMeta.phoneLast4 || '?'})`
              : `${email} downloaded ${module} sheet`,
            resourceId: '',
            resourceType: '',
            metadata: otp
              ? {
                  otpProtectedExport: true,
                  bypassMethod: trackMeta.bypassMethod || '',
                  exportOutcome: 'success',
                  purpose: trackMeta.purpose,
                  phoneLast4: trackMeta.phoneLast4,
                  activityModule: trackMeta.activityModule || module,
                  responseMessage: 'File downloaded',
                }
              : {},
          }).catch(() => {})
        } catch {
          /* non-fatal */
        }
        // ─────────────────────────────────────────────────────────────────────

        return true
      } else {
        throw new Error('File download failed')
      }
    } catch (error) {
      console.error('Error downloading file:', error)
      // Handle error gracefully, e.g., display an error message to the user
      setLoading(false)
      if (axiosLib.isCancel(error)) {
        console.log('Request cancelled:', error.message)
      } else {
        console.error('Error:', error.message)
      }
      return false
    }
  }

  return { loading, downloadFile }
}

export default useFileDownloader
